'use client';

import { useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { IoCheckmark, IoChevronDown, IoRemove } from 'react-icons/io5';

// Assets
import IX from '@public/svg/i-x';

// Components
import Modal from '@src/components/atoms/modal';

const getSelectableIds = (offices) => offices.filter((office) => office.total > 0).map((office) => office.id);

const Checkbox = ({ checked, indeterminate, disabled, onChange, uncheckedClassName = 'bg-white' }) => {
  const stateClass = disabled
    ? 'bg-[#F5F5F5] border-[#F5F5F5] cursor-not-allowed'
    : checked
      ? 'bg-[#252526] border-[#252526] text-white'
      : indeterminate
        ? 'bg-[#E2DCCA] border-[#E2DCCA] text-[#252526]'
        : `${uncheckedClassName} border-[#DEDFDE]`;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      disabled={disabled}
      onClick={onChange}
      className={`w-[16px] h-[16px] min-w-[16px] flex items-center justify-center rounded-[2px] border border-solid ${stateClass}`}
    >
      {checked && !disabled && <IoCheckmark size={12} />}
      {indeterminate && !checked && !disabled && <IoRemove size={12} />}
    </button>
  );
};

const LocationItem = ({ location, selected, onToggleOffices }) => {
  const total = location.offices.reduce((sum, office) => sum + office.total, 0);
  const selectableIds = getSelectableIds(location.offices);
  const selectedCount = selectableIds.filter((id) => selected.includes(id)).length;
  const isDisabled = !selectableIds.length;
  const isChecked = !isDisabled && selectedCount === selectableIds.length;
  const isIndeterminate = selectedCount > 0 && !isChecked;

  return (
    <Accordion.Item
      value={location.id}
      disabled={isDisabled}
      className="mb-[8px] border-[0.5px] border-solid border-[#E6E7E8] rounded-[2px] bg-white hover:border-[#252526] data-[disabled]:hover:border-[#E6E7E8]"
    >
      <Accordion.Header className="h-[48px] flex items-center gap-[8px] py-0 px-[16px] bg-white">
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          disabled={isDisabled}
          uncheckedClassName="bg-[#F5F5F5]"
          onChange={() => onToggleOffices(selectableIds, !isChecked)}
        />
        <Accordion.Trigger className="group flex-1 h-full flex items-center justify-between gap-[8px] text-left disabled:cursor-not-allowed">
          <span className={`font-satoshi text-[14px] ${isDisabled ? 'text-[#C4C4C4]' : 'text-[#252526]'}`}>
            {location.name}{' '}
            <span className={`text-[11px] ${isDisabled ? 'text-[#C4C4C4]' : 'text-[#686A68]'}`}>({total} lowongan)</span>
          </span>
          <IoChevronDown
            size={16}
            className={`min-w-[16px] transition-transform group-data-[state=open]:rotate-180 ${isDisabled ? 'text-[#C4C4C4]' : 'text-[#686A68]'}`}
          />
        </Accordion.Trigger>
      </Accordion.Header>

      <Accordion.Content className="bg-[#FBFBFB]">
        {location.offices.map((office) => {
          const isOfficeDisabled = office.total <= 0;

          return (
            <div
              key={`office-${office.id}`}
              className="h-[48px] flex items-center justify-between gap-[8px] py-[16px] pl-[40px] pr-[16px] border-t-[0.5px] border-solid border-[#E6E7E8]"
            >
              <label className={`flex items-center gap-[8px] ${isOfficeDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <Checkbox
                  checked={selected.includes(office.id)}
                  disabled={isOfficeDisabled}
                  onChange={() => onToggleOffices([office.id], !selected.includes(office.id))}
                />
                <span className="font-satoshi text-[14px] text-[#252526]">{office.name}</span>
              </label>
              <span className="font-satoshi text-[11px] text-[#686A68] whitespace-nowrap">{office.total} lowongan</span>
            </div>
          );
        })}
      </Accordion.Content>
    </Accordion.Item>
  );
};

const ModalFilterKarir = ({ onClose, onApply, locations = [], value = [], ...props }) => {
  const [selected, setSelected] = useState(value);
  const [openItems, setOpenItems] = useState(locations[0] ? [locations[0].id] : []);

  const handleToggleOffices = (ids, isSelect) => {
    setSelected((prev) => (isSelect ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))));
  };

  const handleApply = () => {
    onApply(selected);
    onClose();
  };

  return (
    <Modal {...props} onClose={onClose}>
      <div className="relative z-[42] w-[442px] max-h-[calc(100vh-48px)] flex flex-col p-[24px] rounded-[2px] bg-[#FAFAFA] dd3-lg:w-screen dd3-lg:h-[100dvh] dd3-lg:max-h-none dd3-lg:rounded-none">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-[24px] right-[24px] w-[24px] h-[24px] flex items-center justify-center"
        >
          <IX color="#686A68" />
        </button>

        <p className="mb-[10px] pr-[32px] font-satoshi-bold text-[21px] text-[#252526]">
          Filter
        </p>

        <div className="relative flex-1 min-h-0">
          <div className="h-full max-h-[400px] overflow-y-auto dd3-lg:max-h-none">
            <Accordion.Root type="multiple" value={openItems} onValueChange={setOpenItems}>
              {locations.map((location) => (
                <LocationItem
                  key={`location-${location.id}`}
                  location={location}
                  selected={selected}
                  onToggleOffices={handleToggleOffices}
                />
              ))}
            </Accordion.Root>
          </div>
          <div className="pointer-events-none absolute bottom-0 left-0 w-full h-[48px] bg-gradient-to-t from-[#FAFAFA] to-transparent" />
        </div>

        <div className="flex items-center gap-[16px] mt-[16px]">
          <button
            type="button"
            disabled={!selected.length}
            onClick={() => setSelected([])}
            className="h-[40px] px-[16px] border-[0.5px] border-solid border-[#E6E7E8] rounded-[2px] font-satoshi-bold text-[14px] text-[#252526] hover:bg-[#F2F2F2] disabled:text-[#808285] disabled:hover:bg-transparent"
          >
            Hapus Filter
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 h-[40px] rounded-[2px] bg-[#252526] font-satoshi-bold text-[14px] text-white hover:bg-[#141415]"
          >
            Terapkan Filter
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ModalFilterKarir isOpen={isOpen} {...props} />;
};

export default Wrapper;
